const UTM_SESSION_KEY = "qc_utm_session";
const UTM_STORAGE_KEY = "qc_utm";
const ANALYTICS_ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;
const MARKETING_ATTRIBUTION_KEYS = [
  "gclid",
  "fbclid",
  "ttclid",
] as const;
const ALL_ATTRIBUTION_KEYS = [...ANALYTICS_ATTRIBUTION_KEYS, ...MARKETING_ATTRIBUTION_KEYS] as const;

type AttributionPayload = Partial<Record<(typeof ALL_ATTRIBUTION_KEYS)[number], string>> & {
  captured_at: string;
};

export interface AttributionConsentState {
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
}

function getStorage(kind: "local" | "session") {
  if (typeof window === "undefined") return null;

  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getAllowedAttributionKeys(consent: AttributionConsentState) {
  return [
    ...(consent.analyticsAllowed || consent.marketingAllowed ? ANALYTICS_ATTRIBUTION_KEYS : []),
    ...(consent.marketingAllowed ? MARKETING_ATTRIBUTION_KEYS : []),
  ];
}

function readAttributionFromSearch(search: string, consent: AttributionConsentState) {
  const params = new URLSearchParams(search);
  const payload: Partial<AttributionPayload> = {};

  for (const key of getAllowedAttributionKeys(consent)) {
    const value = params.get(key)?.trim();
    if (value) {
      payload[key] = value.slice(0, 300);
    }
  }

  if (Object.keys(payload).length === 0) return null;

  return {
    ...payload,
    captured_at: new Date().toISOString(),
  } satisfies AttributionPayload;
}

function writeJson(storage: Storage | null, key: string, value: unknown) {
  if (!storage) return;
  storage.setItem(key, JSON.stringify(value));
}

export function sanitizeTrackingSearch(search: string, consent: AttributionConsentState) {
  const params = new URLSearchParams(search);
  let changed = false;

  for (const key of ANALYTICS_ATTRIBUTION_KEYS) {
    if (!consent.analyticsAllowed && !consent.marketingAllowed && params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }

  for (const key of MARKETING_ATTRIBUTION_KEYS) {
    if (!consent.marketingAllowed && params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }

  const serialized = params.toString();
  return {
    changed,
    search: serialized ? `?${serialized}` : "",
  };
}

export function syncMarketingAttribution(consent: AttributionConsentState, search?: string) {
  const sessionStorage = getStorage("session");
  const localStorage = getStorage("local");
  const currentSearch = search ?? (typeof window === "undefined" ? "" : window.location.search);
  const attribution = readAttributionFromSearch(currentSearch, consent);

  if (attribution) {
    writeJson(sessionStorage, UTM_SESSION_KEY, attribution);
    if (consent.marketingAllowed) {
      writeJson(localStorage, UTM_STORAGE_KEY, attribution);
      return;
    }
  }

  if (!consent.analyticsAllowed && !consent.marketingAllowed) {
    sessionStorage?.removeItem(UTM_SESSION_KEY);
  }

  if (!consent.marketingAllowed) {
    localStorage?.removeItem(UTM_STORAGE_KEY);
  }
}

export function hasPersistentMarketingAttribution(storage: Storage | null = getStorage("local")) {
  return Boolean(storage?.getItem(UTM_STORAGE_KEY));
}
